'use client'

import { Alert, AlertDescription, AlertTitle } from '@ui/base/alert'
import { Field, FieldError, FieldLabel, FieldPanel } from '@ui/base/field'
import { Input } from '@ui/base/input'
import { useFormContext } from 'react-hook-form'

import { type FulfillmentFormValues } from '../model/types'
import { FormBlock } from './form-block'

const contactFields = [
  { name: 'websiteUrl', id: 'fulfillment-website', label: 'Сайт', type: 'url' },
  { name: 'phone', id: 'fulfillment-phone', label: 'Телефон', type: 'tel' },
  { name: 'email', id: 'fulfillment-email', label: 'Электронная почта', type: 'email' },
  { name: 'telegram', id: 'fulfillment-telegram', label: 'Telegram', type: 'text' },
  { name: 'max', id: 'fulfillment-max', label: 'MAX', type: 'text' },
] as const

/** Пятый шаг: карта организации и способы связи. */
export const ContactsStep = () => {
  const {
    register,
    formState: { errors },
  } = useFormContext<FulfillmentFormValues>()

  return (
    <>
      <FormBlock
        title="Карточки на картах"
        description="Ссылки необязательны, но они заметно повышают доверие к карточке."
      >
        <Alert variant="info" role="note">
          <AlertTitle>Очень рекомендуется</AlertTitle>
          <AlertDescription>
            По ссылкам мы подтягиваем рейтинг, количество оценок, адрес, отзывы и фотографии организации.
          </AlertDescription>
        </Alert>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field data-invalid={Boolean(errors.yandexMapsUrl?.message)}>
            <FieldLabel htmlFor="fulfillment-yandex-maps">Ссылка на Яндекс Карты</FieldLabel>
            <Input
              id="fulfillment-yandex-maps"
              type="url"
              placeholder="https://yandex.ru/maps/org/..."
              aria-invalid={Boolean(errors.yandexMapsUrl?.message)}
              aria-describedby={errors.yandexMapsUrl?.message ? 'fulfillment-yandex-maps-error' : undefined}
              {...register('yandexMapsUrl')}
            />
            <FieldError id="fulfillment-yandex-maps-error">{errors.yandexMapsUrl?.message}</FieldError>
          </Field>
          <Field data-invalid={Boolean(errors.twoGisUrl?.message)}>
            <FieldLabel htmlFor="fulfillment-two-gis">Ссылка на 2ГИС</FieldLabel>
            <Input
              id="fulfillment-two-gis"
              type="url"
              placeholder="https://2gis.ru/moscow/firm/70000001089422217"
              aria-invalid={Boolean(errors.twoGisUrl?.message)}
              aria-describedby={errors.twoGisUrl?.message ? 'fulfillment-two-gis-error' : undefined}
              {...register('twoGisUrl')}
            />
            <FieldError id="fulfillment-two-gis-error">{errors.twoGisUrl?.message}</FieldError>
          </Field>
        </div>
      </FormBlock>

      <FormBlock title="Контакты" description="Все поля необязательны.">
        {/* Способы связи собраны на подложке: пять необязательных полей подряд иначе выглядят
            продолжением ссылок на карты, а не отдельным блоком. */}
        <FieldPanel className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {contactFields.map(({ name, id, label, type }) => {
            const message = errors[name]?.message
            return (
              <Field key={name} data-invalid={Boolean(message)}>
                <FieldLabel htmlFor={id}>{label}</FieldLabel>
                <Input
                  id={id}
                  type={type}
                  aria-invalid={Boolean(message)}
                  aria-describedby={message ? `${id}-error` : undefined}
                  {...register(name)}
                />
                <FieldError id={`${id}-error`}>{message}</FieldError>
              </Field>
            )
          })}
        </FieldPanel>
      </FormBlock>
    </>
  )
}
